/**
 * String utility functions for common string transformations
 */

/**
 * Converts a string to camelCase format
 * @param str - The string to convert
 * @returns The string in camelCase format
 *
 * @example
 * toCamelCase('student-home') // returns 'studentHome'
 * toCamelCase('around_the_world') // returns 'aroundTheWorld'
 * toCamelCase('StudentProfile') // returns 'studentProfile'
 */
export function toCamelCase(str: string): string {
  return str
    .replace(/[^a-zA-Z0-9]+(.)/g, (_, chr) => chr.toUpperCase())
    .replace(/[^a-zA-Z0-9]+$/g, '')
    .replace(/^[A-Z]/, (chr) => chr.toLowerCase());
}

/**
 * Converts a string to PascalCase format
 * @param str - The string to convert
 * @returns The string in PascalCase format
 *
 * @example
 * toPascalCase('student-home') // returns 'StudentHome'
 * toPascalCase('around_the_world') // returns 'AroundTheWorld'
 */
export function toPascalCase(str: string): string {
  return str
    .replace(/[^a-zA-Z0-9]+(.)/g, (_, chr) => chr.toUpperCase())
    .replace(/^[a-z]/, (chr) => chr.toUpperCase());
}

/**
 * Converts a string to kebab-case format
 * @param str - The string to convert
 * @returns The string in kebab-case format
 *
 * @example
 * toKebabCase('studentHome') // returns 'student-home'
 * toKebabCase('AroundTheWorld') // returns 'around-the-world'
 */
export function toKebabCase(str: string): string {
  return str
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .toLowerCase()
    .replace(/^-+|-+$/g, '');
}

/**
 * Converts a string to snake_case format
 * @param str - The string to convert
 * @returns The string in snake_case format
 *
 * @example
 * toSnakeCase('studentHome') // returns 'student_home'
 * toSnakeCase('AroundTheWorld') // returns 'around_the_world'
 */
export function toSnakeCase(str: string): string {
  return str
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .toLowerCase()
    .replace(/^_+|_+$/g, '');
}

/**
 * Capitalizes the first letter of a string
 * @param str - The string to capitalize
 * @returns The string with the first letter capitalized
 *
 * @example
 * capitalize('hello world') // returns 'Hello world'
 */
export function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Truncates a string to a specified length and adds ellipsis if needed
 * @param str - The string to truncate
 * @param maxLength - The maximum length of the string
 * @param ellipsis - The ellipsis to add (default: '...')
 * @returns The truncated string
 *
 * @example
 * truncate('Hello World', 8) // returns 'Hello...'
 */
export function truncate(str: string, maxLength: number, ellipsis = '...'): string {
  if (str.length <= maxLength) return str;
  if (maxLength < ellipsis.length) return ellipsis;
  return str.slice(0, maxLength - ellipsis.length) + ellipsis;
}
